import React from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Sort, Filter, VirtualScroll } from '@syncfusion/ej2-react-grids';
import { useTranslation } from 'react-i18next';

export interface OperationRow {
  resourceName: string;
  resourceTypeName: string;
  operationName: string;
  productName: string;
  campaignId: string;
  plannedStart: string;
  plannedEnd: string;
  targetStart: string;
  targetEnd: string;
  actualStart: string | null;
  actualEnd: string | null;
  executionStatus?: string;
  completionPercentage?: number;
  duration: number; // in hours
}

/** released = view; optimized = optimized schedule (full); active = current schedule (operations only, with a progress background) */
interface ReleasedScheduleGridProps {
  operations: OperationRow[];
  loading: boolean;
  height?: number;
  mode?: 'released' | 'optimized' | 'active';
  /** The progress column is shown only for schedules with status 'completed' (they were active and have actual values) */
  showProgressColumn?: boolean;
}

const ReleasedScheduleGrid: React.FC<ReleasedScheduleGridProps> = ({
  operations,
  loading,
  height = 350,
  mode = 'released',
  showProgressColumn = false
}) => {
  const { t, i18n } = useTranslation(['common']);
  const isOptimizedOrActive = mode === 'optimized' || mode === 'active';
  const showProgressHighlight = mode === 'active';
  const showProgress = showProgressColumn || mode === 'active';

  const translateStatus = (status: string) => {
    if (!status) return '';
    const camel = status.split('_').map((s, i) => i === 0 ? s.toLowerCase() : s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).join('');
    const key = `form.status.${camel}`;
    const translated = t(key, status);
    return translated !== key ? translated : status;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (i18n.language === 'de') {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
      } else {
        return new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).format(date);
      }
    } catch {
      return dateString;
    }
  };

  const formatDuration = (hours: number) => {
    const totalSeconds = Math.round((hours ?? 0) * 3600);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return [h, m, s].map((v) => v.toString().padStart(2, '0')).join(':');
  };

  const isWithinRange = (start?: string, end?: string) => {
    if (!start || !end) return false;
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return false;
    }
    const now = new Date();
    return now >= startDate && now <= endDate;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div
      className="min-h-0"
      style={{ height: `${height}px`, flex: '0 0 auto' }}
    >
      <GridComponent
        locale="de-DE"
        dataSource={operations || []}
        enableVirtualization={true}
        pageSettings={{ pageSize: 50 }}
        allowSorting={true}
        allowFiltering={true}
        height={height}
        width="100%"
        rowDataBound={showProgressHighlight ? (args: any) => {
          const data = args?.data as OperationRow | undefined;
          if (!data || !args?.row) return;
          if (data.executionStatus === 'COMPLETED') {
            args.row.classList.add('released-schedule-row--completed');
          }
          if (isWithinRange(data.targetStart, data.targetEnd)) {
            args.row.classList.add('released-schedule-row--current');
          }
        } : undefined}
      >
        <ColumnsDirective>
          <ColumnDirective 
            field="resourceName" 
            headerText={t('productionSchedule.releasedScheduleView.resourceName', 'Ressource')}
            width="200"
          />
          <ColumnDirective 
            field="resourceTypeName" 
            headerText={t('productionSchedule.releasedScheduleView.resourceTypeName', 'Ressourcentyp')}
            width="150"
          />
          <ColumnDirective 
            field="operationName" 
            headerText={t('productionSchedule.releasedScheduleView.operationName', 'Vorgang')}
            width="200"
          />
          <ColumnDirective 
            field="productName" 
            headerText={t('productionSchedule.releasedScheduleView.productName', 'Produkt')}
            width="200"
          />
          {!isOptimizedOrActive && (
            <ColumnDirective 
              field="campaignId" 
              headerText={t('productionSchedule.releasedScheduleView.campaignId', 'Kampagne')}
              width="200"
            />
          )}
          <ColumnDirective 
            field="plannedStart" 
            headerText={t('productionSchedule.releasedScheduleView.plannedStart', 'Plan-Start')}
            width="170"
            template={(props: any) => formatDate(props.plannedStart)}
          />
          <ColumnDirective 
            field="plannedEnd" 
            headerText={t('productionSchedule.releasedScheduleView.plannedEnd', 'Plan-Ende')}
            width="170"
            template={(props: any) => formatDate(props.plannedEnd)}
          />
          {isOptimizedOrActive && (
            <ColumnDirective 
              field="executionStatus" 
              headerText={t('productionSchedule.releasedScheduleView.executionStatus', 'Status')}
              width="130"
              template={(props: any) => translateStatus(props.executionStatus || '')}
            />
          )}
          <ColumnDirective 
            field="actualStart" 
            headerText={t('productionSchedule.releasedScheduleView.actualStart', 'Ist-Start')}
            width="170"
            template={(props: any) => formatDate(props.actualStart)}
          />
          <ColumnDirective 
            field="actualEnd" 
            headerText={t('productionSchedule.releasedScheduleView.actualEnd', 'Ist-Ende')}
            width="170"
            template={(props: any) => formatDate(props.actualEnd)}
          />
          {!isOptimizedOrActive && (
            <ColumnDirective 
              field="executionStatus" 
              headerText={t('productionSchedule.releasedScheduleView.executionStatus', 'Status')}
              width="130"
              template={(props: any) => translateStatus(props.executionStatus || '')}
            />
          )}
          {showProgress && (
          <ColumnDirective 
            field="completionPercentage" 
            headerText={t('productionSchedule.releasedScheduleView.completion', 'Fortschritt')}
            width="150"
            template={(props: any) => {
              const row = props.data ?? props.rowData ?? props;
              const pct = row.completionPercentage ?? row.completion_percentage ?? null;
              const numPct = Math.min(100, Math.max(0, Number(pct) || 0));
              const barColor = numPct >= 100 ? '#4caf50' : numPct > 0 ? '#2196f3' : '#e0e0e0';
              return (
                <div className="flex items-center gap-2">
                  <div
                    style={{ flex: 1, height: 16, backgroundColor: '#e0e0e0', borderRadius: 3, overflow: 'hidden', minWidth: 60 }}
                  >
                    <div
                      style={{ width: `${numPct}%`, height: '100%', backgroundColor: barColor, borderRadius: 3, transition: 'width 0.2s ease' }}
                    />
                  </div>
                  <span className="text-sm tabular-nums flex-shrink-0" style={{ minWidth: 36, textAlign: 'right' }}>{Math.round(numPct)}%</span>
                </div>
              );
            }}
          />
          )}
          <ColumnDirective 
            field="duration" 
            headerText={t('productionSchedule.releasedScheduleView.duration', 'Dauer')}
            width="120"
            template={(props: any) => formatDuration(props.duration)}
          />
        </ColumnsDirective>
        <Inject services={[Sort, Filter, VirtualScroll]} />
      </GridComponent>
    </div>
  );
};

export default ReleasedScheduleGrid;

